import { CalendarMonth } from "@mui/icons-material"
import {
  Menu,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
} from "@mui/material"
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon"
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DateTime } from "luxon"
import React, { useState, useEffect } from "react"

import { ReservationDateField } from "@/graphql/graphql"
import i18n from "@/i18n"
import { PresetType, getPresetDates, detectPreset } from "@/utils/date_filter"

type DateFieldType = ReservationDateField.CreatedDate | ReservationDateField.ReservationDate
type RangeType = "from" | "to" | "range"

export interface DateFilterValue {
  field: DateFieldType
  rangeType: RangeType
  from?: string
  to?: string
}

interface DateFilterProps {
  value: DateFilterValue
  onChange: (value: DateFilterValue) => void
}

const DateFilter: React.FC<DateFilterProps> = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const today = DateTime.local()
  const [tempFrom, setTempFrom] = useState<DateTime | null>(value.from ? DateTime.fromISO(value.from) : today)
  const [tempTo, setTempTo] = useState<DateTime | null>(value.to ? DateTime.fromISO(value.to) : today)
  const [selectedPreset, setSelectedPreset] = useState<PresetType>(() =>
    detectPreset(value.from ? DateTime.fromISO(value.from) : null, value.to ? DateTime.fromISO(value.to) : null),
  )

  const open = Boolean(anchorEl)

  useEffect(() => {
    setSelectedPreset(detectPreset(tempFrom, tempTo))
  }, [tempFrom, tempTo])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleFieldChange = (field: DateFieldType) => {
    onChange({ ...value, field })
  }

  const handlePresetClick = (preset: PresetType) => {
    setSelectedPreset(preset)
    const dates = getPresetDates(preset)
    setTempFrom(dates.from)
    setTempTo(dates.to)
  }

  const handleRangeTypeChange = (rangeType: RangeType) => {
    if (rangeType === "range") {
      const fromDate = value.from ? DateTime.fromISO(value.from) : today
      const toDate = value.to ? DateTime.fromISO(value.to) : today
      setTempFrom(fromDate)
      setTempTo(toDate)
      setSelectedPreset(detectPreset(fromDate, toDate))
      setModalOpen(true)
    } else {
      // Store the current date in the filter when selecting "from" or "to"
      const currentDateISO = today.toISODate() || undefined
      if (rangeType === "from") {
        onChange({ ...value, rangeType, from: currentDateISO, to: undefined })
      } else {
        onChange({ ...value, rangeType, from: undefined, to: currentDateISO })
      }
    }
  }

  const handleRangeApply = () => {
    const newFrom = tempFrom?.toISODate() || undefined
    const newTo = tempTo?.toISODate() || undefined
    onChange({
      ...value,
      rangeType: "range",
      from: newFrom,
      to: newTo,
    })
    setModalOpen(false)
    handleClose()
  }

  const handleRangeCancel = () => {
    setModalOpen(false)
  }

  const getRangePreview = () => {
    if (value.rangeType === "range" && value.from && value.to) {
      return `${DateTime.fromISO(value.from).toFormat("MMM d")} - ${DateTime.fromISO(value.to).toFormat("MMM d")}`
    }
    return `${today.toFormat("MMM d")} - ${today.toFormat("MMM d")}`
  }

  return (
    <>
      <Button
        onClick={handleClick}
        size="small"
        aria-label={i18n.t("app.reservations_list.date_filter.label")}
        variant="ui-element"
        color="primary"
        className="px-1.5 py-0 min-w-0"
        data-testid="panel-reservations-date-filter-btn"
      >
        <CalendarMonth />
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <div className="px-4 py-2" data-testid="panel-reservations-date-filter-menu">
          <Typography variant="subtitle2" className="mb-2">
            {i18n.t("app.reservations_list.date_filter.field_type.label")}
          </Typography>
          <RadioGroup
            value={value.field}
            onChange={(e) => handleFieldChange(e.target.value as DateFieldType)}
            className="mb-4"
          >
            <FormControlLabel
              value={ReservationDateField.CreatedDate}
              control={<Radio size="small" />}
              label={i18n.t("app.reservations_list.date_filter.field_type.created_date")}
            />
            <FormControlLabel
              value={ReservationDateField.ReservationDate}
              control={<Radio size="small" />}
              label={i18n.t("app.reservations_list.date_filter.field_type.reservation_date")}
            />
          </RadioGroup>

          <Typography variant="subtitle2" className="mb-2">
            {i18n.t("app.reservations_list.date_filter.range_type.label")}
          </Typography>
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              handleRangeTypeChange("range")
              setTempFrom(value.from ? DateTime.fromISO(value.from) : today)
              setTempTo(value.to ? DateTime.fromISO(value.to) : today)
              setModalOpen(true)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleRangeTypeChange("range")
                setTempFrom(value.from ? DateTime.fromISO(value.from) : today)
                setTempTo(value.to ? DateTime.fromISO(value.to) : today)
                setModalOpen(true)
              }
            }}
            className="mt-1 w-full flex items-center justify-between bg-gray-100 border border-gray-800 rounded px-3 py-2 cursor-pointer hover:border-gray-500"
          >
            <Typography variant="body2" className="text-gray-700">
              {getRangePreview()}
            </Typography>
            <CalendarMonth className="text-gray-800" fontSize="small" />
          </div>
        </div>
      </Menu>

      <Dialog
        open={modalOpen}
        onClose={handleRangeCancel}
        maxWidth="md"
        fullWidth
        data-testid="panel-reservations-date-filter-dialog"
      >
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box className="flex gap-0">
              <Box className="flex flex-col justify-center py-4 pr-4 border-r border-gray-300 min-w-[160px]">
                {(
                  [
                    "today",
                    "yesterday",
                    "last_7_days",
                    "last_30_days",
                    "this_month",
                    "last_month",
                    "custom_range",
                  ] as PresetType[]
                ).map((preset) => (
                  <Button
                    key={preset}
                    onClick={() => handlePresetClick(preset)}
                    data-testid={`panel-reservations-date-filter-preset-${preset}`}
                    className={`justify-start text-left normal-case px-4 py-2 ${
                      selectedPreset === preset ? "font-bold" : "font-normal"
                    }`}
                    sx={{
                      color: "black",
                      fontWeight: selectedPreset === preset ? "bold" : "normal",
                      justifyContent: "flex-start",
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                      },
                    }}
                  >
                    {i18n.t(`app.reservations_list.date_filter.presets.${preset}`)}
                  </Button>
                ))}
              </Box>

              <Box className="flex gap-4 justify-center flex-1 pl-4">
                <Box>
                  <Typography variant="subtitle2" className="mb-2">
                    {i18n.t("app.reservations_list.date_filter.range_type.from")}
                  </Typography>
                  <DateCalendar value={tempFrom} onChange={(newValue) => setTempFrom(newValue)} />
                </Box>
                <Box>
                  <Typography variant="subtitle2" className="mb-2">
                    {i18n.t("app.reservations_list.date_filter.range_type.to")}
                  </Typography>
                  <DateCalendar value={tempTo} onChange={(newValue) => setTempTo(newValue)} />
                </Box>
              </Box>
            </Box>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Box className="flex justify-between w-full">
            <Typography variant="body2" className="self-center">
              {tempFrom?.toFormat("MMM d, yyyy")} - {tempTo?.toFormat("MMM d, yyyy")}
            </Typography>
            <Box className="flex gap-2">
              <Button
                onClick={handleRangeCancel}
                color="inherit"
                data-testid="panel-reservations-date-filter-cancel-btn"
              >
                {i18n.t("app.actions.cancel")}
              </Button>
              <Button
                onClick={handleRangeApply}
                variant="contained"
                data-testid="panel-reservations-date-filter-apply-btn"
              >
                {i18n.t("app.actions.apply")}
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default DateFilter
